import { motion } from 'framer-motion';
import { cn } from '../../lib/cn';
import { useHaptics } from '../../hooks/useHaptics';
import { DURATION, EASE_OUT_IOS, useReducedMotion } from '../../lib/motion';
import { Target, Trophy, Users, User } from './icons';

export type TabKey = 'play' | 'standings' | 'friends' | 'profile';

export const NAV_ITEMS: { key: TabKey; label: string; Icon: typeof Target }[] = [
  { key: 'play', label: 'Play', Icon: Target },
  { key: 'standings', label: 'Standings', Icon: Trophy },
  { key: 'friends', label: 'Friends', Icon: Users },
  { key: 'profile', label: 'Profile', Icon: User }
];

/** Mobile-only floating glass pill tab bar — Liquid Glass / Instagram-style. */
export function TabBar({
  active = 'play',
  onSelect,
  className,
  'data-app-chrome': dataAppChrome
}: {
  active?: TabKey;
  onSelect?: (key: TabKey) => void;
  className?: string;
  'data-app-chrome'?: string;
}) {
  const { impact } = useHaptics();
  const reduce = useReducedMotion();

  const handleSelect = (key: TabKey) => {
    impact(key === active ? 'light' : 'selection');
    onSelect?.(key);
  };

  return (
    <nav aria-label="Main navigation" className={cn('pointer-events-none', className)} data-app-chrome={dataAppChrome}>
      <div className="pointer-events-auto px-safe pb-[max(10px,var(--sab))]">
        <div
          className={cn(
            'mx-auto flex h-[60px] max-w-[min(100%,420px)] items-stretch justify-around rounded-[28px] px-2',
            'border border-white/40 bg-white/70 shadow-glass-nav',
            'backdrop-blur-[28px] backdrop-saturate-[200%]',
            'supports-[backdrop-filter:blur(0)]:bg-white/85'
          )}
        >
          {NAV_ITEMS.map(({ key, label, Icon }) => {
            const on = key === active;
            return (
              <button
                key={key}
                type="button"
                aria-label={label}
                aria-current={on ? 'page' : undefined}
                onClick={() => handleSelect(key)}
                className="tap relative flex min-w-0 flex-1 flex-col items-center justify-center gap-[3px] active:scale-[0.92]"
              >
                {on && (
                  <motion.span
                    layoutId="tab-active-pill"
                    className="absolute inset-y-[7px] inset-x-[6px] rounded-2xl bg-black/[0.07] ring-1 ring-inset ring-black/[0.04]"
                    transition={
                      reduce
                        ? { duration: 0 }
                        : { type: 'tween', duration: DURATION.micro / 1000, ease: EASE_OUT_IOS }
                    }
                  />
                )}
                <Icon
                  width={22}
                  height={22}
                  strokeWidth={on ? 2.4 : 1.8}
                  className={cn(
                    'relative z-10 transition-colors duration-200 ease-ios-out',
                    on ? 'text-ink' : 'text-ink2'
                  )}
                />
                <span
                  className={cn(
                    'relative z-10 text-[10px] leading-none tracking-snug transition-colors duration-200 ease-ios-out',
                    on ? 'font-bold text-ink' : 'font-semibold text-ink2'
                  )}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
